import { useEffect, useState, forwardRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NewEnquiryDialog } from "@/components/QuickCreate";
import { AddClientDialog } from "@/components/AddClientDialog";
import { timeAgo } from "@/lib/format";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useApps, AppSwitcher, AppLauncherButton, AppList } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChatDock } from "@/components/ChatDock";
import { DemoTour, openDemoTour } from "@/components/DemoTour";
import {
  LayoutDashboard, Package, Users, Settings, MessageSquare, ClipboardList, FileText,
  LogOut, Menu, X, ChevronDown, Search, Plus, Bell, ScanLine, CheckSquare, CalendarDays, Mail, Building2, Clock, Boxes, MapPin, Wallet, Loader2, BarChart3, RotateCcw, Megaphone, Bot, Layers, Sun, Route, Warehouse, Truck, SlidersHorizontal
} from "lucide-react";

const NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, end: true, mod: "dashboard" },
  { to: "/inbox", label: "WhatsApp Inbox", icon: MessageSquare, mod: "inbox" },
  { to: "/ai-agent", label: "AI Agent", icon: Bot, mod: "inbox" },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone, mod: "inbox" },
  { to: "/enquiries", label: "Enquiries", icon: ClipboardList, mod: "enquiries" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare },
  { to: "/sales?tab=quotes", label: "Quotes", icon: FileText, match: "/quotes", mod: "sales" },
  { to: "/sales?tab=orders", label: "Sales Orders", icon: FileText, match: "/orders", mod: "sales" },
  { to: "/sales?tab=invoices", label: "Invoices", icon: FileText, match: "/invoices", mod: "sales" },
  { to: "/sales?tab=purchase-orders", label: "Retailer POs", icon: FileText, match: "/purchase-orders", mod: "sales" },
  { to: "/customers", label: "Customers", icon: Users, mod: "customers" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, mod: "dashboard" },
  { to: "/products", label: "Products", icon: Package, mod: "products" },
  { to: "/scan", label: "Scan", icon: ScanLine, mod: "products" },
  { to: "/inventory?tab=overview", label: "Overview", icon: Boxes, mod: "inventory" },
  { to: "/inventory?tab=stock", label: "Stock", icon: Package, mod: "inventory" },
  { to: "/inventory?tab=warehouses", label: "Warehouses", icon: Warehouse, mod: "inventory" },
  { to: "/inventory?tab=purchasing", label: "Purchasing", icon: Truck, mod: "inventory" },
  { to: "/inventory?tab=suppliers", label: "Suppliers", icon: Building2, mod: "inventory" },
  { to: "/inventory?tab=settings", label: "Inventory Settings", icon: SlidersHorizontal, mod: "inventory", manage: true },
  { to: "/slabs", label: "Slab Register", icon: Layers, mod: "inventory" },
  { to: "/field?tab=today", label: "Today", icon: Sun, mod: "field" },
  { to: "/field?tab=accounts", label: "Accounts", icon: Users, mod: "field" },
  { to: "/field?tab=route", label: "Route", icon: Route, mod: "field" },
  { to: "/field?tab=visits", label: "Visits", icon: ClipboardList, match: "/field/visits", mod: "field" },
  { to: "/field?tab=map", label: "Map", icon: MapPin, mod: "field" },
  { to: "/field?tab=analytics", label: "Field Analytics", icon: BarChart3, mod: "field", manage: true },
  { to: "/expenses", label: "Expenses", icon: Wallet, mod: "expenses" },
  { to: "/leads", label: "Leads", icon: Building2, superOnly: true },
  { to: "/settings", label: "Settings", icon: Settings },
];
const BOTTOM = ["Tasks", "WhatsApp Inbox", "Enquiries", "Sales Orders"];
// Per-app primary objects shown directly in the mobile bottom nav (rest go under "More").
const BOTTOM_BY_APP = {
  sales: BOTTOM,
  field: ["Today", "Accounts", "Route", "Visits"],
  inventory: ["Overview", "Stock", "Warehouses", "Purchasing"],
};

const TAB_DEFAULT = { "/sales": "quotes", "/field": "today", "/inventory": "overview" };
function isActive(item, loc) {
  const path = loc.pathname;
  if (item.end) return path === "/";
  if (item.to.includes("?tab=")) {
    const [base, qs] = item.to.split("?");
    const wantTab = new URLSearchParams(qs).get("tab");
    if (path === base) {
      const curTab = new URLSearchParams(loc.search).get("tab") || TAB_DEFAULT[base];
      return curTab === wantTab;
    }
    return item.match ? path.startsWith(item.match) : false;
  }
  return path.startsWith(item.to);
}

const IconBtn = forwardRef(function IconBtn({ children, onClick, title, testid, badge, badgeClass = "bg-[#F83A84]", accent = false, hideMobile = false, ...rest }, ref) {
  return (
    <button ref={ref} onClick={onClick} title={title} data-testid={testid} {...rest}
      className={`relative w-10 h-10 rounded-lg items-center justify-center transition-colors ${hideMobile ? "hidden sm:flex" : "flex"} ${accent ? "text-white border border-white/40 hover:bg-white/15 w-9 h-9 rounded-md" : "text-white/85 hover:bg-white/10 hover:text-white"}`}>
      {children}
      {badge > 0 && <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-[#101D51] ${badgeClass}`} data-testid={`${testid}-badge`}>{badge > 99 ? "99+" : badge}</span>}
    </button>);
});

function GlobalSearch() {
  const [q, setQ] = useState(""); const [res, setRes] = useState(null); const navigate = useNavigate();
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(async () => {
      const [c, e, qs, o, i] = await Promise.all([
        api.get(`/customers?q=${encodeURIComponent(q)}&page_size=4`), api.get(`/enquiries?page_size=200`),
        api.get(`/quotes?q=${encodeURIComponent(q)}&page_size=4`), api.get(`/orders?q=${encodeURIComponent(q)}&page_size=4`), api.get(`/invoices?q=${encodeURIComponent(q)}&page_size=4`)]);
      const ql = q.toLowerCase();
      setRes([
        ...c.data.items.map((x) => ({ k: "Customer", t: x.name, s: `+91 ${x.phone}`, p: `/customers/${x.id}` })),
        ...e.data.items.filter((x) => (x.number + x.customer_name + x.description).toLowerCase().includes(ql)).slice(0, 4).map((x) => ({ k: "Enquiry", t: x.number, s: x.customer_name, p: `/enquiries/${x.id}` })),
        ...qs.data.items.map((x) => ({ k: "Quote", t: x.number, s: x.customer?.name, p: `/quotes/${x.id}` })),
        ...o.data.items.map((x) => ({ k: "Sales Order", t: x.number, s: x.customer?.name, p: `/orders/${x.id}` })),
        ...i.data.items.map((x) => ({ k: "Invoice", t: x.number, s: x.customer?.name, p: `/invoices/${x.id}` })),
      ]);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative w-full max-w-xl">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/60 peer-focus:text-[#6B7189]" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search records" data-testid="global-search"
        className="w-full h-10 pl-9 pr-3 rounded-lg bg-white/10 border border-white/15 text-sm text-white placeholder:text-white/60 focus:outline-none focus:bg-white focus:text-[#101D51] focus:placeholder:text-[#6B7189] transition-colors" />
      {res && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-xl shadow-xl border border-[#E6E8F0] max-h-96 overflow-y-auto" data-testid="global-search-results">
          {res.length === 0 && <div className="p-3 text-sm text-[#6B7189]">No matches</div>}
          {res.map((r) => (
            <button key={`${r.k}-${r.p}`} onClick={() => { navigate(r.p); setQ(""); }} className="w-full text-left px-3 py-2 hover:bg-[#F5F6FA] flex items-center gap-3 border-b border-[#F0F1F5] last:border-0" data-testid={`search-result-${r.t}`}>
              <span className="text-[10px] uppercase tracking-wide w-20 shrink-0 text-[#6B7189]">{r.k}</span>
              <span className="text-sm font-medium text-[#101D51]">{r.t}</span><span className="text-xs text-[#6B7189] truncate">{r.s}</span>
            </button>))}
        </div>)}
    </div>
  );
}

// "+" quick-create and bell popovers.
function HeaderMenus({ counts, setCounts, onNewEnquiry }) {
  const navigate = useNavigate();
  const [pop, setPop] = useState(null);
  const [notifs, setNotifs] = useState(null);
  const loadNotifs = () => api.get("/notifications/mine").then((r) => setNotifs(r.data)).catch(() => {});
  const readAll = async () => { await api.post("/notifications/read-all"); loadNotifs(); setCounts((c) => ({ ...c, notifications: 0 })); };
  const openNotif = async (n) => { setPop(null); if (!n.read) { await api.post(`/notifications/${n.id}/read`); setCounts((c) => ({ ...c, notifications: Math.max(0, (c.notifications || 0) - 1) })); } if (n.link) navigate(n.link); };
  const create = [["New task", CheckSquare, () => navigate("/tasks?new=1"), "task"], ["New enquiry", ClipboardList, onNewEnquiry, "enquiry"],
    ["New quote", FileText, () => navigate("/quotes/new"), "quote"], ["New customer", Users, () => navigate("/customers?new=1"), "customer"]];
  return (
    <>
      <Popover open={pop === "create"} onOpenChange={(o) => setPop(o ? "create" : null)}>
        <PopoverTrigger asChild><IconBtn title="Quick create" testid="topbar-create" accent><Plus className="w-5 h-5" /></IconBtn></PopoverTrigger>
        <PopoverContent align="end" className="w-56 p-1.5" data-testid="quick-create-menu">
          {create.map(([l, Icon, fn, k]) => (
            <button key={k} onClick={() => { setPop(null); fn(); }} className="w-full flex items-center gap-2.5 px-3 min-h-[44px] rounded-md text-sm text-[#101D51] hover:bg-[#F5F6FA]" data-testid={`quick-create-${k}`}><Icon className="w-4 h-4 text-[#6B7189]" /> {l}</button>))}
        </PopoverContent>
      </Popover>
      <Popover open={pop === "bell"} onOpenChange={(o) => { setPop(o ? "bell" : null); if (o) loadNotifs(); }}>
        <PopoverTrigger asChild><IconBtn title="Notifications" testid="topbar-bell" badge={counts.notifications}><Bell className="w-5 h-5" /></IconBtn></PopoverTrigger>
        <PopoverContent align="end" className="w-80 sm:w-96 p-0 max-h-[70vh] overflow-y-auto" data-testid="notifications-menu">
          <div className="px-3 py-2 flex items-center justify-between border-b border-[#E6E8F0]"><span className="font-semibold text-sm text-[#101D51]">Notifications</span>
            {notifs?.unread > 0 && <button onClick={readAll} className="text-xs text-[#1D4ED8]" data-testid="notifications-read-all">Mark all read</button>}</div>
          {!notifs ? <div className="p-4 text-sm text-[#6B7189]">Loading…</div> : notifs.items.length === 0 ? <div className="p-6 text-sm text-[#6B7189] text-center" data-testid="notifications-empty">You're all caught up.</div>
            : notifs.items.map((n) => <NotificationRow key={n.id} n={n} onOpen={openNotif} />)}
        </PopoverContent>
      </Popover>
    </>);
}

function NotificationRow({ n, onOpen }) {
  return (
    <button onClick={() => onOpen(n)} className={`w-full text-left px-3 py-2.5 border-b border-[#F0F1F5] hover:bg-[#F5F6FA] ${n.read ? "opacity-60" : ""}`} data-testid={`notification-${n.id}`}>
      <div className="flex items-start gap-2"><span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-[#F83A84]"}`} />
        <div className="min-w-0"><div className="text-sm font-medium text-[#101D51] truncate">{n.title}</div><div className="text-xs text-[#6B7189] line-clamp-2">{n.body}</div><div className="text-[10px] text-[#9AA0B5] mt-0.5">{timeAgo(n.created_at)}</div></div></div>
    </button>);
}

function UserMenu({ open, setOpen, canSettings }) {
  const { user, tenant, logout, switchTenant } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState(null);
  const [showTenants, setShowTenants] = useState(false);
  const [addClient, setAddClient] = useState(false);
  useEffect(() => {
    if (open && user?.is_super_admin && tenants === null) {
      api.get("/tenants").then(({ data }) => setTenants(data)).catch(() => {});
    }
  }, [open, user, tenants]);
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="w-9 h-9 ml-1 rounded-full bg-[#F83A84] text-white flex items-center justify-center text-sm font-bold" data-testid="user-menu-btn">{user?.name?.[0] || "?"}</button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-white text-[#101D51] rounded-xl shadow-xl border border-[#E6E8F0] p-3 z-50" data-testid="user-menu">
          <div className="font-semibold">{user?.name}</div>
          <div className="text-xs text-[#6B7189]">{user?.role}{user?.is_super_admin ? " · Clotus super-admin" : ""} · {tenant?.name}</div>
          {canSettings && <button onClick={() => { setOpen(false); navigate("/settings"); }} className="mt-3 w-full h-10 rounded-lg border border-[#E6E8F0] text-sm flex items-center gap-2 px-3 hover:bg-[#F5F6FA]" data-testid="menu-settings"><Settings className="w-4 h-4" /> Settings</button>}
          {user?.is_super_admin && <button onClick={() => { setOpen(false); setAddClient(true); }} className="mt-2 w-full h-10 rounded-lg border border-[#F83A84]/40 text-sm text-[#F83A84] font-medium flex items-center gap-2 px-3 hover:bg-[#FFF0F6]" data-testid="add-client-btn"><Plus className="w-4 h-4" /> Onboard new client</button>}
          {user?.is_super_admin && (
            <button onClick={() => setShowTenants((v) => !v)} className="mt-2 w-full h-10 rounded-lg border border-[#E6E8F0] text-sm flex items-center justify-between px-3 hover:bg-[#F5F6FA]" data-testid="switch-tenant-btn">
              <span className="flex items-center gap-2"><Building2 className="w-4 h-4" /> Switch tenant</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showTenants ? "rotate-180" : ""}`} />
            </button>
          )}
          {user?.is_super_admin && showTenants && (
            <div className="mt-1 max-h-48 overflow-y-auto border border-[#E6E8F0] rounded-lg" data-testid="tenant-list">
              {tenants === null ? <div className="px-3 py-2 text-xs text-[#6B7189]">Loading…</div>
                : tenants.length === 0 ? <div className="px-3 py-2 text-xs text-[#6B7189]">No tenants found</div>
                  : tenants.map((t) => (
                    <button key={t.id} onClick={() => { if (t.id !== tenant?.id) switchTenant(t.id); }} disabled={t.id === tenant?.id}
                      className={`block w-full text-left text-sm px-3 py-2 hover:bg-[#F5F6FA] ${t.id === tenant?.id ? "font-semibold text-[#101D51] bg-[#F5F6FA] cursor-default" : "text-[#101D51]"}`}
                      data-testid={`tenant-option-${t.id}`}>
                      {t.name}{t.id === tenant?.id ? " · current" : ""}
                    </button>
                  ))}
            </div>
          )}
          <button onClick={logout} data-testid="logout-btn" className="mt-3 w-full h-10 rounded-lg border border-[#E6E8F0] text-sm flex items-center justify-center gap-2 hover:bg-[#F5F6FA]"><LogOut className="w-4 h-4" /> Log out</button>
        </div>)}
      {user?.is_super_admin && <AddClientDialog open={addClient} onOpenChange={setAddClient} />}
    </div>);
}

function trialDaysLeft(tenant) {
  if (!tenant || (tenant.plan && tenant.plan !== "trial") || !tenant.trial_ends_at) return null;
  const end = new Date(tenant.trial_ends_at).getTime();
  if (Number.isNaN(end)) return null;
  return Math.ceil((end - Date.now()) / 86400000);
}

function tenantExpired(t) {
  if (!t) return false;
  const plan = t.plan || "trial";
  if (plan === "active") return false;
  if (plan === "suspended") return true;
  if (!t.trial_ends_at) return false;
  const end = new Date(t.trial_ends_at).getTime();
  return !Number.isNaN(end) && end <= Date.now();
}

function TrialExpiredGate() {
  const { user, tenant } = useAuth();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  if (user?.is_super_admin || !tenantExpired(tenant)) return null;
  const request = async () => {
    setBusy(true);
    try { await api.post("/access-request", { phone, message }); setSent(true); toast.success("Request sent to the TradeDesk360 team"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-[#101D51]/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="trial-expired-gate">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
        <div className="w-12 h-12 rounded-xl bg-[#FEE2E2] flex items-center justify-center mb-4"><Clock className="w-6 h-6 text-[#B91C1C]" /></div>
        <h2 className="text-xl font-bold text-[#101D51]">Your free trial has expired</h2>
        <p className="text-sm text-[#6B7189] mt-2">Your 15-day trial of TradeDesk360 has ended. To keep using {tenant?.name || "your account"}, please connect with our sales team to activate a plan.</p>
        {!sent ? (
          <div className="mt-4 space-y-3">
            <div><Label className="text-xs">Best number to reach you</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" className="mt-1" data-testid="trial-request-phone" /></div>
            <div><Label className="text-xs">Message</Label><Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Anything you'd like us to know (optional)" className="mt-1" data-testid="trial-request-message" /></div>
            <button onClick={request} disabled={busy} className="w-full h-11 rounded-lg bg-[#F83A84] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#e02d74]" data-testid="trial-request-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request access from sales team"}
            </button>
            <p className="text-center text-xs text-[#6B7189]">or email <a href="mailto:hello@tradedesk360.com" className="text-[#1D4ED8] underline">hello@tradedesk360.com</a></p>
          </div>
        ) : (
          <div className="mt-4 rounded-lg bg-[#DCFCE7] text-[#15803D] p-4 text-sm font-medium text-center" data-testid="trial-request-sent">
            Thanks! Your request has reached the TradeDesk360 team — we'll be in touch shortly.
          </div>
        )}
      </div>
    </div>);
}

function DemoBanner({ tenant }) {
  const { user } = useAuth();
  const [resetting, setResetting] = useState(false);
  if (!tenant?.is_demo) return null;
  const reset = async () => {
    if (!window.confirm("Reset all demo data back to a fresh state? This clears any changes made during the demo.")) return;
    setResetting(true);
    try {
      await api.post("/demo/reset");
      toast.success("Demo data reset — reloading a fresh demo…");
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(apiError(e));
      setResetting(false);
    }
  };
  return (
    <div className="px-4 py-1.5 text-xs sm:text-sm font-medium flex items-center justify-center flex-wrap gap-x-3 gap-y-1 bg-[#101D51] text-white" data-testid="demo-banner">
      <span className="flex items-center gap-2">
        <MessageSquare className="w-4 h-4 shrink-0 text-[#25D366]" />
        Demo mode · WhatsApp uses a shared test number (+1 555-197-5783) — your own number is connected during onboarding. You're in the demo period.
      </span>
      <button onClick={openDemoTour} data-testid="demo-banner-tour"
        className="underline decoration-white/40 underline-offset-2 hover:decoration-white font-semibold whitespace-nowrap">
        Take the tour
      </button>
      {(user?.is_super_admin || user?.role === "Owner") && (
        <button onClick={reset} disabled={resetting} data-testid="demo-banner-reset"
          className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 px-3 py-0.5 font-semibold whitespace-nowrap transition-colors disabled:opacity-60">
          {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
          {resetting ? "Resetting…" : "Reset demo data"}
        </button>
      )}
    </div>
  );
}

function TrialBanner({ days }) {
  if (days === null || days === undefined) return null;
  const expired = days <= 0;
  const cls = expired ? "bg-[#B91C1C] text-white" : days <= 3 ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#EAF3FF] text-[#1D4ED8]";
  return (
    <div className={`px-4 py-1.5 text-xs sm:text-sm font-medium flex items-center justify-center gap-2 ${cls}`} data-testid="trial-banner">
      <Clock className="w-4 h-4 shrink-0" />
      {expired ? "Your free trial has ended — contact Clotus to continue using TradeDesk." : `Free trial · ${days} day${days === 1 ? "" : "s"} left`}
    </div>
  );
}

function BottomNav({ loc, onMore, moreActive, navItems }) {
  const navigate = useNavigate();
  const { active } = useApps();
  const labels = BOTTOM_BY_APP[active?.slug] || BOTTOM;
  const visible = new Set((navItems || []).map((i) => i.label));
  let items = labels.map((l) => NAV.find((i) => i.label === l)).filter((i) => i && visible.has(i.label));
  if (items.length === 0) items = (navItems || []).filter((i) => !i.end).slice(0, 4);
  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-[#E6E8F0] flex pb-safe" data-testid="bottom-nav">
      {items.map((i) => (
        <button key={i.label} onClick={() => navigate(i.to)} data-testid={`bottomnav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={`flex-1 flex flex-col items-center justify-center py-2 min-h-[56px] ${isActive(i, loc) ? "text-[#F83A84]" : "text-[#6B7189]"}`}>
          <i.icon className="w-5 h-5" /><span className="text-[10px] mt-0.5">{i.label.replace("WhatsApp ", "").replace("Sales ", "")}</span>
        </button>))}
      <button onClick={onMore} data-testid="bottomnav-more" className={`flex-1 flex flex-col items-center justify-center py-2 min-h-[56px] ${moreActive ? "text-[#F83A84]" : "text-[#6B7189]"}`}>
        <Menu className="w-5 h-5" /><span className="text-[10px] mt-0.5">More</span>
      </button>
    </div>);
}

export default function AppLayout({ children }) {
  const { user, tenant } = useAuth();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const navigate = useNavigate(); const loc = useLocation();
  const wide = loc.pathname === "/enquiries" || loc.pathname === "/inbox";
  const threadOpen = loc.pathname === "/inbox" && new URLSearchParams(loc.search).get("c");
  const canSettings = user?.is_super_admin || ["Owner", "Manager"].includes(user?.role);
  const modOn = (m) => user?.is_super_admin || !m || (tenant?.modules?.[m] !== false);
  const { activeModules } = useApps();
  const inApp = (m) => !activeModules || !m || activeModules.includes(m);
  const nav = NAV.filter((i) => i.to !== "/settings" && (!i.superOnly || user?.is_super_admin) && (!i.manage || canSettings) && modOn(i.mod) && inApp(i.mod));
  const trialLeft = trialDaysLeft(tenant);
  const [counts, setCounts] = useState({});
  const [quick, setQuick] = useState(null);
  useEffect(() => {
    const f = () => { if (!document.hidden) api.get("/activities/header-counts").then((r) => setCounts(r.data)).catch(() => {}); };
    f(); const t = setInterval(f, 30000); return () => clearInterval(t);
  }, []);

  if (tenant && !user?.is_super_admin && tenantExpired(tenant)) {
    return <TrialExpiredGate />;  // block the app entirely; no page components fire API calls
  }

  return (
    <div className="flex flex-col h-full bg-[#F5F6FA]">
      {/* Top bar */}
      <header className="bg-[#101D51] text-white shrink-0 pt-safe" data-testid="topbar">
        <div className="h-14 flex items-center gap-2 sm:gap-3 px-3 sm:px-4">
          <button className="md:hidden w-10 h-10 -ml-2 flex items-center justify-center" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="w-6 h-6" /></button>
          <div className="flex items-center gap-2 shrink-0">
            <img src="/tradedesk-mark.png" alt="TradeDesk360" className="w-8 h-8 rounded-lg bg-white p-1 object-contain" />
            <span className="font-bold tracking-tight hidden lg:inline" data-testid="brand">TradeDesk360</span>
          </div>
          <div className="flex-1 flex justify-center px-1 sm:px-4"><GlobalSearch /></div>
          <div className="flex items-center gap-0.5 sm:gap-1 shrink-0" data-testid="header-actions">
            <HeaderMenus counts={counts} setCounts={setCounts} onNewEnquiry={() => setQuick("enquiry")} />
            <IconBtn title="Tasks & calendar" onClick={() => navigate("/tasks")} testid="topbar-calendar" badge={counts.tasks_due} badgeClass="bg-[#2563EB]" hideMobile><CalendarDays className="w-5 h-5" /></IconBtn>
            <IconBtn title="WhatsApp inbox" onClick={() => navigate("/inbox")} testid="topbar-inbox" badge={counts.unread_chats}><Mail className="w-5 h-5" /></IconBtn>
            <UserMenu open={menu} setOpen={setMenu} canSettings={canSettings} />
          </div>
        </div>
        {quick === "enquiry" && <NewEnquiryDialog onClose={() => setQuick(null)} />}
        {/* App tabs — App Launcher fixed on the left; only the tabs scroll horizontally */}
        <nav className="hidden md:flex items-stretch bg-[#F8F9FC] text-[#101D51] border-b border-[#E6E8F0] px-3" data-testid="app-tabs">
          <div className="flex items-center pr-3 mr-2 border-r border-[#E6E8F0] shrink-0" data-testid="app-switcher-bar">
            <AppSwitcher tenantName={tenant?.name || "TradeDesk"} />
          </div>
          <div className="flex items-stretch overflow-x-auto flex-1 min-w-0">
            {nav.map((i) => {
              const active = isActive(i, loc);
              return (
                <NavLink key={i.label} to={i.to} data-testid={`nav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-3 h-11 flex items-center text-sm font-medium border-b-[3px] whitespace-nowrap transition-colors ${active ? "border-[#F83A84] text-[#101D51]" : "border-transparent text-[#3B4468] hover:bg-[#F5F6FA]"}`}>
                  {i.label}
                </NavLink>);
            })}
          </div>
        </nav>
      </header>

      <TrialBanner days={trialLeft} />
      <DemoBanner tenant={tenant} />
      <TrialExpiredGate />

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 max-w-[85vw] bg-white p-3 pt-safe pb-safe flex flex-col overflow-y-auto" data-testid="mobile-drawer">
            <div className="flex items-center justify-between mb-3"><span className="font-bold text-[#101D51] truncate">{tenant?.name}</span><button onClick={() => setOpen(false)} className="w-11 h-11 flex items-center justify-center" data-testid="mobile-drawer-close"><X className="w-5 h-5" /></button></div>
            <div className="mb-2" data-testid="mobile-drawer-apps">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#6B7189] px-1 mb-1">Apps</div>
              <AppList onPick={() => setOpen(false)} />
              <div className="h-px bg-[#F0F1F5] my-3" />
            </div>
            {nav.map((i) => <NavLink key={i.label} to={i.to} onClick={() => setOpen(false)} className={`flex items-center gap-3 px-3 min-h-[44px] rounded-lg text-sm font-medium ${isActive(i, loc) ? "bg-[#101D51] text-white" : "text-[#101D51] hover:bg-[#F5F6FA]"}`}><i.icon className="w-5 h-5" />{i.label}</NavLink>)}
          </div>
        </div>)}

      <main className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-3 sm:p-5 lg:p-6 ${threadOpen ? "pb-0" : ""}`} onClick={() => menu && setMenu(false)}>
        <div className={`${wide ? "" : "max-w-[1400px]"} mx-auto animate-fade-up`}>{children}</div>
      </main>
      {/* Reserved strip so pages never run under the fixed utility bar / bottom nav */}
      <div className="hidden md:block h-10 shrink-0" aria-hidden />
      {!threadOpen && <div className="md:hidden shrink-0 h-[calc(3.5rem+env(safe-area-inset-bottom,0px))]" aria-hidden />}
      <ChatDock />
      {tenant?.is_demo && <DemoTour />}

      {/* Floating App Launcher (mobile) — bottom-right, just above the bottom navigation bar */}
      {!threadOpen && (
        <div className="md:hidden fixed right-4 z-[35]" style={{ bottom: "calc(3.5rem + env(safe-area-inset-bottom,0px) + 0.75rem)" }} data-testid="app-launcher-fab-wrap">
          <AppLauncherButton />
        </div>)}
      {!threadOpen && <BottomNav loc={loc} onMore={() => setOpen(true)} moreActive={open} navItems={nav} />}
    </div>
  );
}
